<template>
  <div class="fixed z-50 border border-gray-500" :style="{ top: `${position.y}px`, left: `${position.x-150}px` }">
    <div class="bg-white rounded-lg shadow-xl w-[100vh] h-[50vh] flex flex-col relative">
      <!-- Header with title and close button (draggable) -->
      <div 
        class="flex items-center justify-between p-4 border-b cursor-move"
        @mousedown="startDrag"
        @touchstart="startDrag">
        <div class="w-4">
          <i class="fas fa-grip-lines"></i>
        </div>
        <span class="font-mono text-lg font-medium text-center flex-grow">
            <a :href="`https://polymarket.com/event/${eventSlug}`" target="_blank">{{ title }} <i class="fa-solid fa-link"></i></a>
        </span>
        <button @click="$emit('close')" class="text-gray-500 hover:text-gray-700 w-4">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <!-- Interval selector buttons -->
      <div class="flex justify-between p-3 bg-gray-50 border-b">
        <div class="flex space-x-2">
          <button 
            v-for="interval in intervals" 
            :key="interval.value" 
            @click="changeInterval(interval.value)"
            :class="[
              'px-3 py-1 text-sm rounded',
              currentInterval === interval.value 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
            ]"
          >
            {{ interval.label }}
          </button>
        </div>
        
        <!-- Autoscale toggle buttons -->
        <div class="flex items-center">
          <span class="mr-2 text-sm">Autoscale:</span>
          <div class="flex space-x-1">
            <button 
              @click="toggleAutoscale(true)"
              :class="[
                'px-3 py-1 text-sm rounded',
                isAutoscaleEnabled 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
              ]"
            >
              ON
            </button>
            <button 
              @click="toggleAutoscale(false)"
              :class="[
                'px-3 py-1 text-sm rounded',
                !isAutoscaleEnabled 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
              ]"
            >
              OFF
            </button>
          </div>
        </div>
      </div>
      
      <!-- Chart container -->
      <div class="flex-1 p-4 w-[100vh] h-[50vh] overflow-hidden">
        <div ref="chartContainer" class="w-full h-full" ></div>
      </div>
      
      <!-- Loading indicator -->
      <div v-if="loading" class="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center">
        <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { createChart, PriceScaleMode } from 'lightweight-charts';
import { parseISO } from 'date-fns';
import apiClient from '../utils/axios';
import { sortedUniqBy } from 'lodash';
import { UserPriceAlerts } from '../tlc_plugins/user-price-alerts';
import * as Sentry from '@sentry/vue';
export default {
  props: {
    marketId: String,
    clobTokenId: String,
    marketCreatedAt: String,
    eventId: String,
    eventSlug: String,
    title: {
      type: String,
      default: 'Price History'
    }
  },
  data() {
    return {
      chart: null,
      areaSeries: null,
      loading: false,
      currentInterval: 'max',
      isAutoscaleEnabled: true,
      intervals: [
        { label: '1h', value: '1h' },
        { label: '6h', value: '6h' },
        { label: '1d', value: '1d' },
        { label: '1w', value: '1w' },
        { label: '1m', value: '1m' },
        { label: 'Max', value: 'max' }
      ],
      chartData: [],
      resizeObserver: null,
      position: {
        x: window.innerWidth / 2 - 400, // Center the 800px width window
        y: window.innerHeight / 2 - 300, // Approximate vertical centering
      },
      isDragging: false,
      dragOffset: { x: 0, y: 0 }
    };
  },
  watch: {
    clobTokenId: {
      async handler(newVal) {
        console.log('clobTokenId changed to:', newVal);
        await this.loadChart(true);
      },
    },
  },
  async mounted() {
    await this.loadChart(true);
    
    // Add global event listeners for drag
    document.addEventListener('mousemove', this.onDrag);
    document.addEventListener('mouseup', this.stopDrag);
    document.addEventListener('touchmove', this.onDrag);
    document.addEventListener('touchend', this.stopDrag);
  },
  beforeUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.chart) {
      this.chart.remove();
      this.chart = null;
    }
    
    // Remove global event listeners
    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.stopDrag);
    document.removeEventListener('touchmove', this.onDrag);
    document.removeEventListener('touchend', this.stopDrag);
  },
  methods: {
    initChart() {
        if (this.areaSeries) {
            this.chart.removeSeries(this.areaSeries);
            this.areaSeries = null;
        }

        if (this.chart) {
            this.chart.remove();
            this.chart = null;
        }

      const container = this.$refs.chartContainer;
      // Get explicit dimensions of container
      const width = container.clientWidth;
      const height = container.clientHeight;
      
      const chartOptions = {
        width,
        height,
        layout: {
          textColor: '#333',
          background: { color: '#ffffff' }
        },
        grid: {
          vertLines: { color: '#f0f0f0' },
          horzLines: { color: '#f0f0f0' }
        },

        timeScale: {
          borderColor: '#ddd',
          timeVisible: true,
          fixLeftEdge: true,
          fixRightEdge: true,
        },
        
        crosshair: {
          mode: 1
        }
      };

      this.chart = createChart(container, chartOptions);

      if (this.isAutoscaleEnabled) {
        this.addScaledSeries();
      } else {
        this.addFullScaleSeries();
      }
      
      // Make chart responsive
      this.resizeObserver = new ResizeObserver(entries => {
        if (entries.length === 0 || !entries[0].contentRect || !this.chart) return;
        const { width, height } = entries[0].contentRect;
        this.chart.applyOptions({ width, height });
      });
      
      this.resizeObserver.observe(this.$refs.chartContainer);
    },

    addScaledSeries() {
        if (this.areaSeries && this.chart) {
            this.chart.removeSeries(this.areaSeries)
        }

        this.areaSeries = this.chart.addAreaSeries({
        lineColor: '#2962FF',
        topColor: 'rgba(41, 98, 255, 0.56)',
        bottomColor: 'rgba(41, 98, 255, 0.04)',
        lineWidth: 2,
        priceFormat: {
          type: 'percent',
          precision: 2,
          minMove: 0.01
        },
      });
    },

    addFullScaleSeries() {
        if (this.areaSeries && this.chart) {
            this.chart.removeSeries(this.areaSeries)
        }

        this.areaSeries = this.chart.addAreaSeries({
        lineColor: '#2962FF',
        topColor: 'rgba(41, 98, 255, 0.56)',
        bottomColor: 'rgba(41, 98, 255, 0.04)',
        lineWidth: 2,
        priceFormat: {
          type: 'percent',
          precision: 2,
          minMove: 0.01
        },
        // SeriesOptionsCommon
        autoscaleInfoProvider: () => ({
            priceRange: {
                minValue: 0,
                maxValue: 100,
            },
            margins: {
                above: 0,
                below: 0,
            },
        }),
      });

      this.areaSeries.priceScale().applyOptions({
        scaleMargins: {
            top: 0.05,
            bottom: 0.05,
        },
      });
    },

    async loadChart(refetch = false) {
        this.initChart();
        if (refetch) {
            await this.fetchData();
        }
        this.areaSeries.setData(this.chartData);
        this.addPriceAlerts();
    },

    addPriceAlerts() {
        if (this.areaSeries) {
        // Price alerts
        const userPriceAlertsPrimitive = new UserPriceAlerts();
        // Override the addAlert method
        //const originalAddAlert = userPriceAlertsPrimitive.addAlert.bind(userPriceAlertsPrimitive);
        userPriceAlertsPrimitive.addAlert = (price) => {
          this.$emit('graph-alert', this.eventId, this.marketId, price);
          // Call the original method and return its result
          //return originalAddAlert(price);
          return '123';
        };
        userPriceAlertsPrimitive.setSymbolName('Alert');
        this.areaSeries.attachPrimitive(userPriceAlertsPrimitive);

        userPriceAlertsPrimitive.alertAdded().subscribe((alertInfo) => {
            console.log(`Alert added @ ${alertInfo.price} with the id: ${alertInfo.id}`);
        });

        userPriceAlertsPrimitive.alertRemoved().subscribe((id) => {
            console.log(`Alert removed with the id: ${id}`);
        });

        // addAlert(price: number): string {
        // removeAlert(id: string) {


        setTimeout(() => {
            this.chart.timeScale().fitContent();
        }, 50);
        } else {
            console.error('Area series not initialized');
        }
    },

    calculateFidelity() {
        console.log('Calculating fidelity for market created at:', this.marketCreatedAt);
        const endTime = new Date().toISOString();
        const startTimeTimestamp = parseISO(this.marketCreatedAt).getTime() / 1000; // Convert to UTC timestamp in seconds
        const endTimeTimestamp = parseISO(endTime).getTime() / 1000; // Convert event end date to UTC timestamp in seconds
        const eventAge = endTimeTimestamp - startTimeTimestamp; // Event age in seconds

        let fidelity;
        if (eventAge <= 86400) { // up to 1 day old
            fidelity = 5; // 5 minutes
        } else if (eventAge <= 7 * 86400) { // from 1 day to 1 week old
            fidelity = 15; // 15 minutes
        } else if (eventAge <= 30 * 86400) { // from 1 week to 1 month
            fidelity = 60; // 1 hour
        } else if (eventAge <= 6 * 30 * 86400) { // from 1 month to 6 months
            fidelity = 720; // 12 hours
        } else { // from 6 months and up
            fidelity = 1440; // 24 hours
        }
        return fidelity;
    },

    async fetchData() {
      this.loading = true;
      try {
        const endpoint = 'https://clob.polymarket.com/prices-history'; // actual endpoint
        const params = {
          market: this.clobTokenId,
          interval: this.currentInterval,
          fidelity: this.calculateFidelity()
        };
        
        const response = await apiClient.get(endpoint, { params });

        if (!response.data || !response.data.history || response.data.history.length === 0) {
          console.warn('No price data available for this market');
          this.loading = false;
          return;
        }

        const uniqTs = sortedUniqBy(response.data.history, point => point.t);

        // lightweight-charts expects Unix timestamp in seconds (if number) or specific string formats
        this.chartData = uniqTs.map(point => {
          // If t is milliseconds timestamp, convert to seconds
          let timestamp = point.t;
          if (timestamp > 1000000000000) { // If in milliseconds (13 digits)
            timestamp = Math.floor(timestamp / 1000); // Convert to seconds
          }
          
          return {
            time: timestamp,
            value: point.p * 100
          };
        });
      } catch (error) {
        Sentry.setExtra('method', 'fetchData');
        Sentry.captureException(error);
        console.error('Error fetching price history:', error);
      } finally {
        this.loading = false;
      }
    },
    
    changeInterval(interval) {
      this.currentInterval = interval;
      this.fetchData();
    },
    
    startDrag(event) {
      this.isDragging = true;
      
      // Calculate the offset from the mouse position to the top-left corner of the component
      const clientX = event.clientX || (event.touches && event.touches[0].clientX);
      const clientY = event.clientY || (event.touches && event.touches[0].clientY);
      
      this.dragOffset = {
        x: clientX - this.position.x,
        y: clientY - this.position.y
      };
      
      // Prevent text selection during drag
      event.preventDefault();
    },
    
    onDrag(event) {
      if (!this.isDragging) return;
      
      const clientX = event.clientX || (event.touches && event.touches[0].clientX);
      const clientY = event.clientY || (event.touches && event.touches[0].clientY);
      
      this.position = {
        x: clientX - this.dragOffset.x,
        y: clientY - this.dragOffset.y
      };
    },
    
    stopDrag() {
      this.isDragging = false;
    },
    
    async toggleAutoscale(enabled) {
        console.log('Toggling autoscale to:', enabled);
        this.isAutoscaleEnabled = enabled;

        await this.loadChart(false);
    }
  }
};
</script> 